import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import DataTable from '../components/ui/DataTable';
import '../styles/ProductList.css'; // Standard page stylesheet

function PurchaseOrderList() {
    const { hasPermission } = usePermissionContext();
    const navigate = useNavigate();

    const getRolePrefix = () => {
        const user = JSON.parse(sessionStorage.getItem('user'));
        if (!user || !user.role) return '';
        if (user.role === 'SUPER_ADMIN') return '/super-admin';
        if (user.role === 'ADMIN') return '/admin';
        return '/user';
    };

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const fetchLowStockProducts = async () => {
        try {
            setLoading(true);
            const data = await fetchApi('/product?low_stock=true&limit=10000');
            setRows(data.products || []);
        } catch (error) {
            console.error("Fetch Low Stock Products Error:", error);
            if (error.status !== 403) {
                toast.error(`Failed to load low stock products: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLowStockProducts();
    }, []);

    // Local client-side filter for search query
    const filteredRows = useMemo(() => {
        if (!search) return rows;
        const q = search.toLowerCase();
        return rows.filter(r => 
            (r.productName || '').toLowerCase().includes(q) ||
            (r.productModelNumber || '').toLowerCase().includes(q) ||
            (r.categoryName || '').toLowerCase().includes(q) ||
            String(r.productSalePrice || '').includes(q) ||
            String(r.minimumStock || '').includes(q) ||
            String(r.productStock || '').includes(q)
        );
    }, [rows, search]);

    const sortedRows = useMemo(() => {
        let sortableItems = [...filteredRows];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'productSalePrice' || sortConfig.key === 'minimumStock' || sortConfig.key === 'productStock' || sortConfig.key === 'purchaseOrder') {
                    aValue = parseFloat(aValue) || 0;
                    bValue = parseFloat(bValue) || 0;
                } else {
                    aValue = aValue ? aValue.toString().toLowerCase() : '';
                    bValue = bValue ? bValue.toString().toLowerCase() : '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [filteredRows, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // Pagination calculations
    const totalEntries = sortedRows.length;
    const totalPages = Math.ceil(totalEntries / pageSize) || 1;
    
    // Ensure active page is within bounds
    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);

    const pagedRows = useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return sortedRows.slice(startIndex, startIndex + pageSize);
    }, [sortedRows, page, pageSize]);

    const showingFrom = totalEntries > 0 ? (page - 1) * pageSize + 1 : 0;
    const showingTo = Math.min(page * pageSize, totalEntries);

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
    }

    // Export to Excel
    const handleExportExcel = () => {
        if (sortedRows.length === 0) {
            toast.warn("No data to export");
            return;
        }

        const exportData = sortedRows.map(r => ({
            'Name': r.productName || '',
            'Part Number': r.productModelNumber || '',
            'MRP': (parseFloat(r.productSalePrice) || 0).toFixed(2),
            'Minimum': r.minimumStock || 0,
            'Available': r.productStock || 0,
            'Order': r.purchaseOrder || 0
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Product Order List');
        XLSX.writeFile(workbook, 'Product_Order_List.xlsx');
    };

    // Print helper
    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        const totalAvailable = sortedRows.reduce((sum, r) => sum + (Number(r.productStock) || 0), 0);
        const totalOrder = sortedRows.reduce((sum, r) => sum + (Number(r.purchaseOrder) || 0), 0);

        printWindow.document.write(`
            <html>
                <head>
                    <title>Product Order List</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
                        th { background-color: #f2f2f2; }
                        h2 { text-align: center; }
                        .total-row { font-weight: bold; background-color: #f9f9f9; }
                    </style>
                </head>
                <body>
                    <h2>Product Order List</h2>
                    <table>
                        <thead>
                            <tr>
                                <th>NAME</th>
                                <th>PART NUMBER</th>
                                <th>MRP</th>
                                <th>MINIMUM</th>
                                <th>AVAILABLE</th>
                                <th>ORDER</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${sortedRows.map((r, idx) => `
                                <tr>
                                    <td>${r.productName || ''}</td>
                                    <td>${r.productModelNumber || '-'}</td>
                                    <td>${(parseFloat(r.productSalePrice) || 0).toFixed(2)}</td>
                                    <td>${r.minimumStock || 0}</td>
                                    <td style="color: #ef4444; font-weight: bold;">${r.productStock || 0}</td>
                                    <td style="font-weight: bold;">${r.purchaseOrder || 0}</td>
                                </tr>
                            `).join('')}
                            <tr class="total-row">
                                <td colspan="4" style="text-align: right;">TOTAL:</td>
                                <td style="color: #ef4444;">${totalAvailable}</td>
                                <td>${totalOrder}</td>
                            </tr>
                        </tbody>
                    </table>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };

    if (loading) {
        return <PageSkeleton />;
    }

    const columns = [
        {
            key: 'productName',
            label: 'NAME',
            sortable: true,
            style: { width: '40%' },
            render: (r) => (
                <span 
                    className="product-name-link" 
                    style={{ cursor: 'pointer', color: '#2563eb', textDecoration: 'underline' }}
                    onClick={() => navigate(`${getRolePrefix()}/product-detail/${r.id || r._id || r.productId}`)}
                >
                    {r.productName}
                </span>
            )
        },
        {
            key: 'productModelNumber',
            label: 'PART NUMBER',
            sortable: true,
            render: (r) => r.productModelNumber || '-'
        },
        {
            key: 'productSalePrice',
            label: 'MRP',
            sortable: true,
            render: (r) => (parseFloat(r.productSalePrice) || 0).toFixed(2)
        },
        {
            key: 'minimumStock',
            label: 'MINIMUM',
            sortable: true,
            render: (r) => r.minimumStock || 0
        },
        {
            key: 'productStock',
            label: 'AVAILABLE',
            sortable: true,
            render: (r) => (
                <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
                    {r.productStock || 0}
                </span>
            )
        },
        {
            key: 'purchaseOrder',
            label: 'ORDER',
            sortable: true,
            render: (r) => (
                <span style={{ fontWeight: '600' }}>
                    {r.purchaseOrder || 0}
                </span>
            )
        }
    ];

    const footerRow = pagedRows.length > 0 ? (
        <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>
            <td colSpan={4} style={{ textAlign: 'right', padding: '12px 10px', color: '#1e293b' }}>TOTAL</td>
            <td style={{ color: '#ef4444', padding: '12px 10px' }}>
                {pagedRows.reduce((sum, r) => sum + (Number(r.productStock) || 0), 0)}
            </td>
            <td style={{ color: '#1e293b', padding: '12px 10px' }}>
                {pagedRows.reduce((sum, r) => sum + (Number(r.purchaseOrder) || 0), 0)}
            </td>
        </tr>
    ) : null;

    return (
        <div className="list-page-container">
            {/* Header: Title and Action Buttons */}
            <div className="list-page-header">
                <h1 className="list-page-title">Product Order List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('Purchase Order', 'report') && (
                        <>
                            <button className="list-page-btn list-page-btn-excel" onClick={handleExportExcel}>
                                EXCEL
                            </button>
                            <button className="list-page-btn list-page-btn-print" onClick={handlePrint}>
                                PRINT
                            </button>
                        </>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={sortedRows}
                sortConfig={sortConfig}
                onSort={requestSort}
                loading={loading}
                emptyMessage="No low stock items found."
                page={page}
                pageSize={pageSize}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                footerRow={footerRow}
                searchBar={
                    <SearchBar
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1);
                        }}
                        placeholder="Search by name, part number, MRP, minimum, available..."
                    />
                }
            />
        </div>
    );
}

export default PurchaseOrderList;
